// copy of packages/integrations/databricks-ai/frontend/src/iac.ts, for the
// Redshift (AI) connector, without outbound identity federation, and its
// clusters
import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import { parseClusterId } from "@p0security/integrations-redshift-ai-shared/components";
import {
  AGENT_ROLE_PATH,
  REDSHIFT_AI_CONNECTOR_FUNCTION_NAME,
} from "@p0security/integrations-redshift-ai-shared/constants";
import { RedshiftAiImages } from "@p0security/integrations-redshift-ai-shared/images";
import type {
  RedshiftAiComponents,
  RedshiftAiIntegration,
} from "@p0security/integrations-redshift-ai-shared/types";
import type { DevEnv } from "@p0security/shared/types/environment";
import objectHash from "object-hash";

type Component = keyof RedshiftAiComponents;

type Item<K extends Component> = RedshiftAiIntegration[K][string];

type TargetType = Item<"cluster">["type"]["type"];

/**
 * The connector image the Terraform copies into the customer's ECR, named as
 * the Terraform provider's `p0_redshift_ai_connector` example names it. Its
 * tag is the `sha-<commit>` tag that p0-security-perimeter's docker workflow
 * pushes for each merge to main, and its digest is the one that tag resolves
 * to. Bumping them changes only the Terraform shown for new installs.
 */
const CONNECTOR_IMAGE = RedshiftAiImages.connector;

/**
 * The connector Lambda's timeout, in seconds. A revoke reads and changes each
 * database of the cluster in turn, which can take longer than the 30 seconds
 * P0's other connector Lambdas get. P0's AWS client cuts an invocation off at
 * exactly 300 seconds (`requestTimeout` in
 * packages/integrations/aws/backend/src/credentials.ts), so the Lambda times
 * out 30 seconds before that.
 */
const CONNECTOR_TIMEOUT_SECONDS = 270;

/** How long the connector's logs are kept, in days. */
const CONNECTOR_LOG_RETENTION_DAYS = 30;

/** Tags the connector's resources, as P0's other connectors tag theirs. */
const P0_TAGS = `{ ManagedBy = "Terraform", ManagedFor = "P0" }`;

/**
 * The database user the connector's execution role signs in as. Redshift
 * names a user that signs in with IAM `IAMR:<role name>`.
 */
export const CONNECTOR_DATABASE_USER = `IAMR:${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}`;

/**
 * A Terraform string literal holding `value`. JSON escapes quotes, backslashes
 * and control characters the way HCL does, and `$${` and `%%{` stop a value
 * from opening an interpolation or directive.
 *
 * A missing value renders as an empty string, so that an item saved without a
 * field its Terraform needs still renders, rather than break the page that
 * shows it.
 */
export const toHclString = (value: string | undefined) =>
  // Replacer functions, because `$$` in a replacement string means one `$`.
  JSON.stringify(value ?? "")
    .replace(/\$\{/g, () => "$${")
    .replace(/%\{/g, () => "%%{");

/**
 * The name of every resource, data source and provider alias one item's
 * Terraform declares. It is unique per item, so the blocks for several items
 * can share a configuration, and another item's blocks can refer to them.
 */
export const toTfName = (component: Component, id: string) =>
  `p0_redshift_ai_${component}_${objectHash({ id }).slice(0, 8)}`;

/**
 * The Terraform and provider versions every item's Terraform needs. docker
 * reads the digest the connector image's tag resolves to, as P0's Docker Hub
 * connector does.
 *
 * The Terraform version is in a block of its own, so that a configuration that
 * already has a `required_providers` block can take this one as it is.
 */
export const PROVIDERS_TERRAFORM = `terraform {
  # 1.4 added terraform_data.
  required_version = ">= 1.4"
}

terraform {
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = ">= 6.0"
    }
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}`;

/**
 * The connector in AWS account `awsAccountId`: its Lambda, outside any VPC,
 * the Lambda's execution role and log group, and P0's role's permission to
 * invoke it. The execution role gets only its log permissions here: each
 * cluster's Terraform lets it into that cluster.
 */
export const renderConnectorTerraform = (
  context: DevEnv,
  awsAccountId: string,
  item: Pick<Item<"connector">, "region">
) => {
  const { region } = item;
  const name = toTfName("connector", awsAccountId);
  const provider = `aws.${name}`;
  const { digest, imageName, tag } = CONNECTOR_IMAGE;
  const repositoryUrl = `aws_ecr_repository.${name}.repository_url`;
  const sourceImage = `local.${name}_image_name`;
  const imageTag = `local.${name}_image_tag`;
  const imageDigest = `local.${name}_image_digest`;

  return `locals {
  # The connector image's version: the sha- tag P0 publishes each release
  # under, and the digest that tag must resolve to. Docker Hub lets a tag be
  # pushed again, so the plan fails if the tag no longer resolves to this
  # digest. To update, take both from the latest p0_redshift_ai_connector
  # example in P0's Terraform registry docs: they change together. Changing
  # them copies the image again and redeploys the Lambda.
  ${name}_image_name   = "${imageName}"
  ${name}_image_tag    = "${tag}"
  ${name}_image_digest = "${digest}"
}

# P0 invokes the connector as the ${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME} Lambda
# in this account and region, so deploy it exactly there. Add the credential
# settings your other AWS providers use, such as profile or assume_role, if
# they have any.
provider "aws" {
  alias               = "${name}"
  region              = ${toHclString(region)}
  allowed_account_ids = [${toHclString(awsAccountId)}]
}

# The execution role. Redshift names its database user
# ${CONNECTOR_DATABASE_USER}, which each cluster's setup SQL creates.
resource "aws_iam_role" "${name}" {
  provider = ${provider}
  name     = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}"
  tags     = ${P0_TAGS}
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

# The log group Lambda writes the connector's logs to.
resource "aws_cloudwatch_log_group" "${name}" {
  provider          = ${provider}
  name              = "/aws/lambda/${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}"
  retention_in_days = ${CONNECTOR_LOG_RETENTION_DAYS}
  tags              = ${P0_TAGS}
}

# The connector writes its own logs, and no others.
resource "aws_iam_role_policy" "${name}_logs" {
  provider = ${provider}
  name     = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}-logs"
  role     = aws_iam_role.${name}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.${name}.arn}:*"
    }]
  })
}

data "docker_registry_image" "${name}" {
  name = "\${${sourceImage}}:\${${imageTag}}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == ${imageDigest}
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Take both from the latest p0_redshift_ai_connector example in P0's Terraform registry docs, or contact support@p0.dev."
    }
  }
}

# Lambda only runs images from Amazon ECR, so this copies P0's image into a
# repository in this account. It needs docker and the AWS CLI, signed in to this
# account, wherever Terraform runs.
resource "aws_ecr_repository" "${name}" {
  provider = ${provider}
  name     = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}"
  # Tags can't be overwritten, so no one who can push to this repository can
  # replace the image that administers Redshift as the execution role.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true
  tags                 = ${P0_TAGS}

  image_scanning_configuration {
    scan_on_push = true
  }
}

# Copies the pinned image again whenever its tag or digest changes, which rolls
# out a new connector release, tagged with that tag and this copy's id. ECR's
# tags can't be overwritten, so each copy needs a tag of its own: copying the
# image again, to roll back or after an interrupted apply, pushes a new one.
resource "terraform_data" "${name}" {
  triggers_replace = {
    digest         = data.docker_registry_image.${name}.sha256_digest
    repository_url = ${repositoryUrl}
    tag            = ${imageTag}
  }

  provisioner "local-exec" {
    # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
    # A failed sign-out doesn't fail the copy once the image is pushed.
    command = <<-EOT
      set -e
      trap 'docker logout "$REGISTRY" || true' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = ${toHclString(region)}
      REGISTRY = split("/", ${repositoryUrl})[0]
      SOURCE   = "\${${sourceImage}}@\${${imageDigest}}"
      TARGET   = "\${${repositoryUrl}}:\${${imageTag}}-\${self.id}"
    }
  }
}

# Pulling one platform and pushing it can give ECR a different manifest from
# the one the tag names on Docker Hub, so the digest there can differ from the
# one pinned above. Lambda needs ECR's.
data "aws_ecr_image" "${name}" {
  provider        = ${provider}
  repository_name = aws_ecr_repository.${name}.name
  image_tag       = "\${${imageTag}}-\${terraform_data.${name}.id}"
}

resource "aws_lambda_function" "${name}" {
  provider      = ${provider}
  function_name = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}"
  role          = aws_iam_role.${name}.arn
  package_type  = "Image"
  image_uri     = "\${${repositoryUrl}}@\${data.aws_ecr_image.${name}.image_digest}"
  architectures = ["x86_64"]
  timeout       = ${CONNECTOR_TIMEOUT_SECONDS}
  tags          = ${P0_TAGS}

  # The connector's role can't create a log group, so its own must exist
  # before it runs.
  depends_on = [aws_cloudwatch_log_group.${name}]
}

# Lets P0 invoke the connector, through the role P0's AWS integration assumes in
# this account.
resource "aws_iam_role_policy" "${name}_p0_invoke_connector" {
  provider = ${provider}
  name     = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}-invoke"
  role     = "${awsRoleName(context, "iam-write")}"
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.${name}.arn
    }]
  })
}

# Lets P0 make and remove each grant's own IAM role, on the
# ${AGENT_ROLE_PATH} path only, through the same role.
resource "aws_iam_role_policy" "${name}_p0_manage_agent_roles" {
  provider = ${provider}
  name     = "${REDSHIFT_AI_CONNECTOR_FUNCTION_NAME}-agent-roles"
  role     = "${awsRoleName(context, "iam-write")}"
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = [
        "iam:CreateRole",
        "iam:TagRole",
        "iam:UpdateAssumeRolePolicy",
        "iam:PutRolePolicy",
        "iam:DeleteRolePolicy",
        "iam:DeleteRole",
      ]
      Resource = "arn:aws:iam::${awsAccountId}:role${AGENT_ROLE_PATH}*"
    }]
  })
}`;
};

/** What differs between a provisioned cluster's Terraform and a Serverless
 * workgroup's. */
type TargetTerraform = {
  /** The data source that finds the target by name, and its ARN. */
  lookup: (args: { name: string; provider: string; target: string }) => {
    block: string;
    arn: string;
  };
  /** The statement that lets the execution role sign in. */
  signIn: (args: {
    account: string;
    arn: string;
    region: string;
    target: string;
  }) => string;
  /** The action that looks the target up, which install validation calls. */
  describe: string;
};

const targetTerraform: Record<TargetType, TargetTerraform> = {
  provisioned: {
    lookup: ({ name, provider, target }) => ({
      block: `data "aws_redshift_cluster" "${name}" {
  provider           = ${provider}
  cluster_identifier = ${toHclString(target)}
}`,
      arn: `data.aws_redshift_cluster.${name}.arn`,
    }),
    // Every database of the cluster, since the connector reads and revokes
    // in each one.
    signIn: ({ account, region, target }) => `      {
        Sid      = "SignIn"
        Effect   = "Allow"
        Action   = "redshift:GetClusterCredentialsWithIAM"
        Resource = "arn:aws:redshift:${region}:${account}:dbname:${target}/*"
      },`,
    describe: "redshift:DescribeClusters",
  },
  serverless: {
    lookup: ({ name, provider, target }) => ({
      block: `data "aws_redshiftserverless_workgroup" "${name}" {
  provider       = ${provider}
  workgroup_name = ${toHclString(target)}
}`,
      arn: `data.aws_redshiftserverless_workgroup.${name}.arn`,
    }),
    // IAM can't name a database of a workgroup: the SQL grants limit what
    // the user reaches.
    signIn: ({ arn }) => `      {
        Sid      = "SignIn"
        Effect   = "Allow"
        Action   = "redshift-serverless:GetCredentials"
        Resource = ${arn}
      },`,
    describe: "redshift-serverless:GetWorkgroup",
  },
};

/**
 * Lets the connector's execution role into one cluster or workgroup: sign
 * in, run statements through the Data API, and look the target up. One inline
 * policy on the role, so removing the cluster's Terraform removes it, and the
 * connector's Terraform never changes when a cluster is added.
 */
export const renderClusterTerraform = (
  clusterId: string,
  item: Pick<Item<"cluster">, "connector" | "type">
) => {
  const parts = parseClusterId(clusterId);
  // The component's validator refuses any other id, so this renders nothing
  // only for a malformed saved item.
  if (!parts) return "";
  const { account, region, name: target } = parts;
  const name = toTfName("cluster", clusterId);
  const provider = `aws.${name}`;
  const variant = targetTerraform[item.type.type];
  const lookup = variant.lookup({ name, provider, target });

  return `# The cluster's region, which may differ from the connector's.
provider "aws" {
  alias               = "${name}"
  region              = ${toHclString(region)}
  allowed_account_ids = [${toHclString(account)}]
}

${lookup.block}

resource "aws_iam_role_policy" "${name}" {
  provider = ${provider}
  name     = ${toHclString(`p0-redshift-ai-${region}-${target}`)}
  role     = aws_iam_role.${toTfName("connector", item.connector)}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [
${variant.signIn({ account, arn: lookup.arn, region, target })}
      {
        Sid      = "DataApi"
        Effect   = "Allow"
        Action   = ["redshift-data:ExecuteStatement", "redshift-data:BatchExecuteStatement"]
        Resource = ${lookup.arn}
      },
      {
        # The Data API checks that a statement is the caller's own.
        Sid      = "Statements"
        Effect   = "Allow"
        Action   = ["redshift-data:DescribeStatement", "redshift-data:GetStatementResult", "redshift-data:CancelStatement"]
        Resource = "*"
      },
      {
        Sid      = "Lookup"
        Effect   = "Allow"
        Action   = "${variant.describe}"
        Resource = ${lookup.arn}
      },
    ]
  })
}`;
};

/**
 * The one-time setup, to run as a superuser: the connector's database user,
 * which may manage users and roles (`sys:secadmin`), and the system
 * permissions revoke needs to find an agent's grants and to hand over or drop
 * what an agent created.
 */
export const SETUP_SQL = `CREATE USER "${CONNECTOR_DATABASE_USER}" PASSWORD DISABLE;
GRANT ROLE "sys:secadmin" TO "${CONNECTOR_DATABASE_USER}";
CREATE ROLE p0_connector_admin;
GRANT ACCESS SYSTEM TABLE, ALTER TABLE, DROP SCHEMA, DROP FUNCTION, DROP PROCEDURE TO ROLE p0_connector_admin;
GRANT ROLE p0_connector_admin TO "${CONNECTOR_DATABASE_USER}";`;
